import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Smartphone, Palette, Bell, Rocket, Shield, Apple, Settings2, Send,
  ChevronRight, Check, X, Eye, RefreshCw, Users, Package, ShoppingCart, Megaphone
} from 'lucide-react';
import axios from 'axios';
import { AppBannerTab } from '../../components/admin/AppBannerTab';

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const TABS = [
  { id: 'branding', label: 'Branding', icon: Palette },
  { id: 'notifications', label: 'Push', icon: Bell },
  { id: 'deployment', label: 'Despliegue', icon: Rocket },
  { id: 'features', label: 'Features', icon: Settings2 },
  { id: 'banner', label: 'Banner y Links', icon: Smartphone },
];

// Color picker component
const ColorField = ({ label, value, onChange }) => (
  <div className="flex items-center gap-3">
    <input type="color" value={value || '#000000'} onChange={e => onChange(e.target.value)} className="w-10 h-10 rounded-lg border border-gray-600 cursor-pointer bg-transparent p-0.5" />
    <div className="flex-1">
      <p className="text-sm text-gray-300">{label}</p>
      <p className="text-xs text-gray-500 font-mono">{value}</p>
    </div>
  </div>
);

// Toggle switch
const Toggle = ({ label, desc, checked, onChange }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-700/50 last:border-0">
    <div>
      <p className="text-sm font-medium text-gray-200">{label}</p>
      {desc && <p className="text-xs text-gray-500 mt-0.5">{desc}</p>}
    </div>
    <button onClick={() => onChange(!checked)} className={`relative w-11 h-6 rounded-full transition-colors ${checked ? 'bg-orange-500' : 'bg-gray-600'}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${checked ? 'translate-x-5' : ''}`} />
    </button>
  </div>
);

export default function AdminAppConfig() {
  const [tab, setTab] = useState('branding');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushTarget, setPushTarget] = useState('all');
  const [sendingPush, setSendingPush] = useState(false);

  useEffect(() => { loadConfig(); loadStats(); }, []);

  const loadConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/app-config`, { headers: getHeaders() });
      setConfig(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadStats = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/app-config/stats`, { headers: getHeaders() });
      setStats(data);
    } catch {}
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/admin/app-config`, config, { headers: getHeaders() });
      setConfig(data);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const sendPush = async () => {
    if (!pushTitle.trim() || !pushBody.trim()) return;
    setSendingPush(true);
    try {
      await axios.post(`${API}/api/admin/app-config/send-push`, { title: pushTitle, body: pushBody, target: pushTarget }, { headers: getHeaders() });
      setPushTitle(''); setPushBody('');
      loadConfig();
    } catch (e) { console.error(e); }
    finally { setSendingPush(false); }
  };

  const updateNested = (path, value) => {
    const keys = path.split('.');
    setConfig(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-64 text-gray-400">Cargando...</div></AdminLayout>;

  const b = config?.branding || {};
  const n = config?.notifications || {};
  const d = config?.deployment || {};
  const f = config?.features || {};
  const ios = d.ios || {};
  const android = d.android || {};

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto" data-testid="admin-app-config">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Smartphone className="w-7 h-7 text-orange-500" />
              App Movil
            </h1>
            <p className="text-gray-400 mt-1">Administra la configuracion, tema, push y despliegue</p>
          </div>
          {tab !== 'banner' && <Button onClick={saveConfig} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white px-6" data-testid="save-config-btn">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </Button>}
        </div>

        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Usuarios', value: stats.total_users, icon: Users, color: 'text-blue-400' },
              { label: 'Productos activos', value: stats.active_products, icon: Package, color: 'text-green-400' },
              { label: 'Ordenes', value: stats.total_orders, icon: ShoppingCart, color: 'text-purple-400' },
              { label: 'Campanas activas', value: stats.active_campaigns, icon: Megaphone, color: 'text-amber-400' },
            ].map(s => (
              <Card key={s.label} className="bg-gray-800 border-gray-700 p-4">
                <div className="flex items-center gap-3">
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                  <div>
                    <p className="text-2xl font-bold text-white">{s.value}</p>
                    <p className="text-xs text-gray-400">{s.label}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                tab === t.id ? 'bg-orange-500 text-white' : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {tab === 'banner' && <AppBannerTab />}
        {tab === 'branding' && (
          <div className="grid grid-cols-2 gap-6">
            <Card className="bg-gray-800 border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-6">Identidad de la App</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 block mb-1.5">Nombre de la App</label>
                  <Input value={b.app_name || ''} onChange={e => updateNested('branding.app_name', e.target.value)} className="bg-gray-700 border-gray-600 text-white" data-testid="input-app-name" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1.5">Tagline</label>
                  <Input value={b.tagline || ''} onChange={e => updateNested('branding.tagline', e.target.value)} className="bg-gray-700 border-gray-600 text-white" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1.5">Estilo de icono</label>
                  <div className="flex gap-2">
                    {['adaptive', 'flat', 'rounded'].map(s => (
                      <button key={s} onClick={() => updateNested('branding.icon_style', s)}
                        className={`px-4 py-2 rounded-lg text-sm capitalize ${b.icon_style === s ? 'bg-orange-500 text-white' : 'bg-gray-700 text-gray-300'}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            <Card className="bg-gray-800 border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-6">Paleta de colores</h3>
              <div className="space-y-4">
                <ColorField label="Color primario" value={b.primary_color} onChange={v => updateNested('branding.primary_color', v)} />
                <ColorField label="Color secundario" value={b.secondary_color} onChange={v => updateNested('branding.secondary_color', v)} />
                <ColorField label="Color acento" value={b.accent_color} onChange={v => updateNested('branding.accent_color', v)} />
                <ColorField label="Fondo" value={b.background_color} onChange={v => updateNested('branding.background_color', v)} />
                <ColorField label="Splash screen" value={b.splash_bg_color} onChange={v => updateNested('branding.splash_bg_color', v)} />
              </div>
            </Card>

            {/* Live preview */}
            <Card className="bg-gray-800 border-gray-700 p-6 col-span-2">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><Eye className="w-5 h-5 text-orange-500" /> Vista previa</h3>
              <div className="flex justify-center gap-8">
                <div className="w-[200px] rounded-[24px] overflow-hidden border-4 border-gray-600 shadow-2xl" style={{ backgroundColor: b.background_color }}>
                  <div style={{ backgroundColor: b.primary_color }} className="px-4 py-3 flex items-center justify-between">
                    <span className="text-white text-sm font-bold">{b.app_name}</span>
                    <Bell className="w-4 h-4 text-white/70" />
                  </div>
                  <div className="p-3">
                    <div className="bg-white rounded-lg p-2 mb-2 flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: b.primary_color }} />
                      <span className="text-[10px] text-gray-400">Buscar productos...</span>
                    </div>
                    <div className="text-[9px] font-bold mb-1" style={{ color: b.accent_color }}>Recomendados</div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[1,2,3,4].map(i => (
                        <div key={i} className="bg-white rounded-lg p-1.5">
                          <div className="h-12 rounded bg-gray-200 mb-1" />
                          <div className="h-1.5 rounded bg-gray-200 mb-0.5 w-3/4" />
                          <div className="h-2 rounded font-bold text-[8px]" style={{ color: b.primary_color }}>$999</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ backgroundColor: b.secondary_color }} className="flex justify-around py-2.5">
                    {['home', 'search', 'cart', 'tag', 'person'].map(icon => (
                      <div key={icon} className="w-5 h-5 rounded-full" style={{ backgroundColor: b.accent_color + '33' }} />
                    ))}
                  </div>
                </div>

                <div className="w-[200px] rounded-[24px] overflow-hidden border-4 border-gray-600 shadow-2xl flex items-center justify-center" style={{ backgroundColor: b.splash_bg_color }}>
                  <div className="text-center">
                    <p className="text-2xl font-extrabold text-white">{b.app_name?.split(' ')[0]}</p>
                    <p className="text-2xl font-extrabold" style={{ color: b.accent_color }}>{b.app_name?.split(' ').slice(1).join(' ')}</p>
                    <p className="text-[10px] text-white/70 mt-1">{b.tagline}</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {tab === 'notifications' && (
          <div className="grid grid-cols-2 gap-6">
            <Card className="bg-gray-800 border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Configuracion de alertas</h3>
              <Toggle label="Push Notifications" desc="Habilitar notificaciones push globales" checked={n.push_enabled} onChange={v => updateNested('notifications.push_enabled', v)} />
              <Toggle label="Alerta de nueva orden" desc="Notificar al vendedor cuando recibe una orden" checked={n.new_order_alert} onChange={v => updateNested('notifications.new_order_alert', v)} />
              <Toggle label="Alerta de nueva pregunta" desc="Notificar al vendedor cuando recibe una pregunta" checked={n.new_question_alert} onChange={v => updateNested('notifications.new_question_alert', v)} />
              <Toggle label="Alertas de promociones" desc="Enviar ofertas y descuentos a compradores" checked={n.promotion_alerts} onChange={v => updateNested('notifications.promotion_alerts', v)} />
              <Toggle label="Estado de repartidor" desc="Alertar cambios de estado de envio" checked={n.driver_status_alerts} onChange={v => updateNested('notifications.driver_status_alerts', v)} />
            </Card>

            <div className="space-y-6">
              <Card className="bg-gray-800 border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><Send className="w-5 h-5 text-orange-500" /> Enviar Push</h3>
                <div className="space-y-3">
                  <Input placeholder="Titulo de la notificacion" value={pushTitle} onChange={e => setPushTitle(e.target.value)} className="bg-gray-700 border-gray-600 text-white" data-testid="push-title-input" />
                  <Textarea placeholder="Cuerpo del mensaje..." value={pushBody} onChange={e => setPushBody(e.target.value)} className="bg-gray-700 border-gray-600 text-white" rows={3} />
                  <div>
                    <label className="text-sm text-gray-400 block mb-1.5">Destinatarios</label>
                    <div className="flex gap-2">
                      {[{v:'all',l:'Todos'},{v:'buyers',l:'Compradores'},{v:'sellers',l:'Vendedores'}].map(o => (
                        <button key={o.v} onClick={() => setPushTarget(o.v)}
                          className={`px-3 py-1.5 rounded-lg text-sm ${pushTarget === o.v ? 'bg-orange-500 text-white' : 'bg-gray-700 text-gray-300'}`}>
                          {o.l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Button onClick={sendPush} disabled={sendingPush || !pushTitle.trim() || !pushBody.trim()} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="send-push-btn">
                    <Send className="w-4 h-4 mr-2" />
                    {sendingPush ? 'Enviando...' : 'Enviar notificacion'}
                  </Button>
                </div>
              </Card>

              <Card className="bg-gray-800 border-gray-700 p-6 max-h-72 overflow-y-auto">
                <h3 className="text-sm font-semibold text-gray-400 mb-3">Historial de push</h3>
                {(config?.push_history || []).length === 0 ? (
                  <p className="text-sm text-gray-500">No hay notificaciones enviadas</p>
                ) : (
                  <div className="space-y-2">
                    {(config?.push_history || []).map((p, i) => (
                      <div key={i} className="bg-gray-700/50 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-white">{p.title}</p>
                          <Badge variant="outline" className="text-[10px] border-gray-600 text-gray-400">{p.target}</Badge>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">{p.body}</p>
                        <p className="text-[10px] text-gray-500 mt-1">{new Date(p.sent_at).toLocaleString('es-MX')}</p>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </div>
        )}

        {tab === 'deployment' && (
          <div className="space-y-6">
            {/* iOS */}
            <Card className="bg-gray-800 border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-6">
                <Apple className="w-6 h-6 text-white" />
                <h3 className="text-lg font-semibold text-white">iOS / App Store</h3>
                <Badge className={`ml-auto ${ios.status === 'published' ? 'bg-green-600' : ios.status === 'review' ? 'bg-yellow-600' : 'bg-gray-600'}`}>
                  {ios.status === 'published' ? 'Publicada' : ios.status === 'review' ? 'En revision' : 'Borrador'}
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-gray-400 block mb-1.5">Bundle ID</label>
                  <Input value={ios.bundle_id || ''} onChange={e => updateNested('deployment.ios.bundle_id', e.target.value)} className="bg-gray-700 border-gray-600 text-white font-mono text-sm" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1.5">Nombre en App Store</label>
                  <Input value={ios.app_store_name || ''} onChange={e => updateNested('deployment.ios.app_store_name', e.target.value)} className="bg-gray-700 border-gray-600 text-white" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1.5">URL App Store</label>
                  <Input value={ios.app_store_url || ''} onChange={e => updateNested('deployment.ios.app_store_url', e.target.value)} placeholder="https://apps.apple.com/..." className="bg-gray-700 border-gray-600 text-white text-sm" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1.5">Version</label>
                  <Input value={ios.version || ''} onChange={e => updateNested('deployment.ios.version', e.target.value)} className="bg-gray-700 border-gray-600 text-white" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1.5">Build Number</label>
                  <Input value={ios.build_number || ''} onChange={e => updateNested('deployment.ios.build_number', e.target.value)} className="bg-gray-700 border-gray-600 text-white" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1.5">iOS Minimo</label>
                  <Input value={ios.minimum_os || ''} onChange={e => updateNested('deployment.ios.minimum_os', e.target.value)} className="bg-gray-700 border-gray-600 text-white" />
                </div>
              </div>
              <div className="mt-4">
                <label className="text-sm text-gray-400 block mb-2">Permisos</label>
                <div className="flex flex-wrap gap-2">
                  {(ios.permissions || []).map(p => (
                    <Badge key={p} variant="outline" className="border-gray-600 text-gray-300 text-xs">{p}</Badge>
                  ))}
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700" onClick={() => updateNested('deployment.ios.status', 'review')}>
                  Enviar a revision
                </Button>
                <Button variant="outline" className="border-green-600 text-green-400 hover:bg-green-900/20" onClick={() => updateNested('deployment.ios.status', 'published')}>
                  Marcar publicada
                </Button>
              </div>
            </Card>

            {/* Android */}
            <Card className="bg-gray-800 border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-6 h-6 text-green-400" />
                <h3 className="text-lg font-semibold text-white">Android / Play Store</h3>
                <Badge className={`ml-auto ${android.status === 'published' ? 'bg-green-600' : android.status === 'review' ? 'bg-yellow-600' : 'bg-gray-600'}`}>
                  {android.status === 'published' ? 'Publicada' : android.status === 'review' ? 'En revision' : 'Borrador'}
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-gray-400 block mb-1.5">Package Name</label>
                  <Input value={android.package_name || ''} onChange={e => updateNested('deployment.android.package_name', e.target.value)} className="bg-gray-700 border-gray-600 text-white font-mono text-sm" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1.5">Nombre en Play Store</label>
                  <Input value={android.play_store_name || ''} onChange={e => updateNested('deployment.android.play_store_name', e.target.value)} className="bg-gray-700 border-gray-600 text-white" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1.5">URL Play Store</label>
                  <Input value={android.play_store_url || ''} onChange={e => updateNested('deployment.android.play_store_url', e.target.value)} placeholder="https://play.google.com/..." className="bg-gray-700 border-gray-600 text-white text-sm" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1.5">Version Name</label>
                  <Input value={android.version_name || ''} onChange={e => updateNested('deployment.android.version_name', e.target.value)} className="bg-gray-700 border-gray-600 text-white" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1.5">Version Code</label>
                  <Input value={android.version_code || ''} onChange={e => updateNested('deployment.android.version_code', e.target.value)} className="bg-gray-700 border-gray-600 text-white" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1.5">Target SDK</label>
                  <Input value={android.target_sdk || ''} onChange={e => updateNested('deployment.android.target_sdk', e.target.value)} className="bg-gray-700 border-gray-600 text-white" />
                </div>
              </div>
              <div className="mt-4">
                <label className="text-sm text-gray-400 block mb-2">Permisos</label>
                <div className="flex flex-wrap gap-2">
                  {(android.permissions || []).map(p => (
                    <Badge key={p} variant="outline" className="border-gray-600 text-gray-300 text-xs">{p}</Badge>
                  ))}
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700" onClick={() => updateNested('deployment.android.status', 'review')}>
                  Enviar a revision
                </Button>
                <Button variant="outline" className="border-green-600 text-green-400 hover:bg-green-900/20" onClick={() => updateNested('deployment.android.status', 'published')}>
                  Marcar publicada
                </Button>
              </div>
            </Card>

            {/* Build instructions */}
            <Card className="bg-gray-800 border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><Rocket className="w-5 h-5 text-orange-500" /> Generar Build</h3>
              <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm text-gray-300 space-y-2">
                <p className="text-gray-500"># Instalar EAS CLI</p>
                <p>npm install -g eas-cli</p>
                <p className="text-gray-500 mt-3"># Iniciar sesion en Expo</p>
                <p>eas login</p>
                <p className="text-gray-500 mt-3"># Build para Android (APK)</p>
                <p>cd /app/mobile && eas build --platform android --profile preview</p>
                <p className="text-gray-500 mt-3"># Build para iOS</p>
                <p>cd /app/mobile && eas build --platform ios --profile preview</p>
                <p className="text-gray-500 mt-3"># Build de produccion</p>
                <p>eas build --platform all --profile production</p>
                <p className="text-gray-500 mt-3"># Enviar a las tiendas</p>
                <p>eas submit --platform android</p>
                <p>eas submit --platform ios</p>
              </div>
            </Card>
          </div>
        )}

        {tab === 'features' && (
          <div className="grid grid-cols-2 gap-6">
            <Card className="bg-gray-800 border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Feature Flags</h3>
              <Toggle label="Anuncios patrocinados" desc="Mostrar ads en la app" checked={f.ads_enabled} onChange={v => updateNested('features.ads_enabled', v)} />
              <Toggle label="Tracking de envios" desc="Seguimiento en tiempo real de repartidores" checked={f.delivery_tracking} onChange={v => updateNested('features.delivery_tracking', v)} />
              <Toggle label="Busqueda por proximidad" desc="Ordenar productos por cercania" checked={f.proximity_search} onChange={v => updateNested('features.proximity_search', v)} />
              <Toggle label="Modo oscuro" desc="Permitir a usuarios cambiar a dark mode" checked={f.dark_mode_available} onChange={v => updateNested('features.dark_mode_available', v)} />
            </Card>

            <Card className="bg-gray-800 border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Control de versiones</h3>
              <Toggle label="Modo mantenimiento" desc="Bloquea la app y muestra mensaje de mantenimiento" checked={f.maintenance_mode} onChange={v => updateNested('features.maintenance_mode', v)} />
              <Toggle label="Forzar actualizacion" desc="Obliga a los usuarios a actualizar la app" checked={f.force_update} onChange={v => updateNested('features.force_update', v)} />
              <div className="mt-4">
                <label className="text-sm text-gray-400 block mb-1.5">Version minima requerida</label>
                <Input value={f.minimum_app_version || ''} onChange={e => updateNested('features.minimum_app_version', e.target.value)} className="bg-gray-700 border-gray-600 text-white" />
              </div>
              <div className="mt-4">
                <label className="text-sm text-gray-400 block mb-1.5">Mensaje de actualizacion forzada</label>
                <Textarea value={f.force_update_message || ''} onChange={e => updateNested('features.force_update_message', e.target.value)} className="bg-gray-700 border-gray-600 text-white" rows={2} />
              </div>
            </Card>

            <Card className="bg-gray-800 border-gray-700 p-6 col-span-2">
              <h3 className="text-lg font-semibold text-white mb-4">API</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-gray-400 block mb-1.5">URL Base del API</label>
                  <Input value={config?.api?.base_url || ''} onChange={e => updateNested('api.base_url', e.target.value)} placeholder="https://api.zonalibre.com" className="bg-gray-700 border-gray-600 text-white font-mono text-sm" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1.5">Timeout (segundos)</label>
                  <Input type="number" value={config?.api?.timeout_seconds || 30} onChange={e => updateNested('api.timeout_seconds', parseInt(e.target.value))} className="bg-gray-700 border-gray-600 text-white" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1.5">Cache TTL (minutos)</label>
                  <Input type="number" value={config?.api?.cache_ttl_minutes || 5} onChange={e => updateNested('api.cache_ttl_minutes', parseInt(e.target.value))} className="bg-gray-700 border-gray-600 text-white" />
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
